import React, { useRef, useEffect, useState } from "react";
import {
  MessageCircle,
  X,
  Minimize2,
  Maximize2,
  Minimize,
  // RotateCcw,
  // History,
} from "lucide-react";
import { WelcomeScreen } from "./WelcomeScreen";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
// import { ConversationSidebar } from "./ConversationSidebar";
import { Button } from "../ui/Button";
import { ChatWidgetConfig } from "./index";

interface ChatWindowProps {
  chat: {
    messages: any[];
    isLoading: boolean;
    isConnected: boolean;
    conversationId: string;
    messagesEndRef: React.RefObject<HTMLDivElement>;
    sendMessage: (message: string) => void;
    clearMessages: () => void;
  };
  onClose: () => void;
  onMinimize?: () => void;
  onMaximize?: () => void;
  isMaximized?: boolean;
  style?: React.CSSProperties;
  config?: ChatWidgetConfig;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  chat,
  onClose,
  onMinimize,
  onMaximize,
  isMaximized = false,
  style,
  config = {},
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showConversationSidebar, setShowConversationSidebar] = useState(false);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (chat.messagesEndRef?.current) {
      chat.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat.messages, isMaximized]);

  // Show sidebar only when maximized
  const sidebarVisible = isMaximized && showConversationSidebar;

  const windowStyle = style || {};
  const mergedStyle = {
    ...windowStyle,
    zIndex: config.zIndex || windowStyle.zIndex || 50,
  };

  const isDark = config?.theme?.themeMode === "dark";
  const baseWindowClasses =
    "relative h-full w-full flex flex-col overflow-hidden border animate-in slide-in-from-bottom duration-300 sm:animate-in sm:slide-in-from-right";
  const themeClasses = isDark
    ? "bg-[#14161C] border-[#1C1F24] text-gray-200"
    : "bg-white border-gray-200 text-gray-800";

  return (
    <div
      className={`fixed ${
        isMaximized
          ? "inset-2 sm:inset-4 lg:inset-6"
          : "inset-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:w-96 sm:h-[600px] md:w-[420px] md:h-[650px] lg:w-[400px] lg:h-[500px]"
      }`}
      style={mergedStyle}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50 sm:hidden"
        onClick={onClose}
      />

      <div
        className={`${baseWindowClasses} ${themeClasses} ${
          isMaximized ? "rounded-lg shadow-2xl" : "sm:rounded-2xl sm:shadow-2xl"
        }`}
      >
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDark
              ? "border-[#1C1F24] bg-[#1C1F24] text-gray-200"
              : "border-gray-200 bg-white text-gray-800"
          } ${isMaximized ? "rounded-t-lg" : "sm:rounded-t-2xl"}`}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              {chat.isLoading && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3
                  className={`font-semibold truncate ${isDark ? "text-gray-100" : "text-gray-800"}`}
                >
                  {config.botName || "NopalCyber Assistant"}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      chat.isConnected
                        ? "bg-green-500 animate-pulse"
                        : "bg-red-500"
                    }`}
                  ></div>
                </div>
                <p className="text-xs text-gray-500">
                  {chat.isLoading
                    ? "Thinking..."
                    : chat.isConnected
                      ? "Online"
                      : "Offline"}
                </p>
                {!chat.isConnected && (
                  <div className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                    Reconnecting...
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* {isMaximized && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setShowConversationSidebar(!showConversationSidebar)
                }
                className={`${isDark ? "w-9 h-9" : "w-8 h-8 "} p-0 rounded-md transition-colors ${
                  showConversationSidebar
                    ? isDark
                      ? "text-orange-400"
                      : "text-orange-600"
                    : isDark
                      ? "text-gray-300 hover:text-white hover:bg-gray-300/5"
                      : "text-gray-500 hover:text-gray-700"
                }`}
                title="Conversation history"
              >
                <History className="w-5 h-5" />
              </Button>
            )} */}
            {/* {chat.messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={chat.clearMessages}
                className={`w-8 h-8 p-0 transition-colors ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Clear conversation"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            )} */}
            {onMaximize && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMaximize}
                className={`hidden sm:flex p-0 transition-colors hover:text-gray-700 w-10 h-10  ${
                  isDark
                    ? "text-gray-300 hover:text-white hover:bg-gray-300/5"
                    : "text-gray-500"
                }`}
                title={isMaximized ? "Restore window" : "Maximize window"}
              >
                {isMaximized ? (
                  <Minimize className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </Button>
            )}
            {onMinimize && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMinimize}
                className={`hidden sm:flex p-0 transition-colors hover:text-gray-700 w-10 h-10 ${
                  isDark
                    ? "text-gray-300 hover:text-white hover:bg-gray-300/5"
                    : "text-gray-500"
                }`}
                title="Minimize window"
              >
                <Minimize2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className={`p-0 transition-colors w-10 h-10 ${
                isDark
                  ? "text-gray-300 hover:text-red-400 hover:bg-gray-300/5"
                  : "text-gray-500 hover:text-red-600"
              }`}
              title="Close chat"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-1 overflow-hidden">
          {/* Conversation Sidebar */}
          {/* <ConversationSidebar
            isVisible={sidebarVisible}
            onClose={() => setShowConversationSidebar(false)}
            currentConversationId={chat.conversationId}
            themeMode={config?.theme?.themeMode}
          />

          {/* Chat Messages Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto scroll-smooth">
              {chat.messages.length === 0 ? (
                <WelcomeScreen
                  themeMode={config?.theme?.themeMode}
                  accessMode={config.accessMode}
                />
              ) : (
                <MessageList
                  messages={chat.messages}
                  isLoading={chat.isLoading}
                  messagesEndRef={chat.messagesEndRef}
                  isMaximized={isMaximized}
                  themeMode={config?.theme?.themeMode}
                />
              )}
            </div>

            {/* Message Input */}
            <MessageInput
              ref={inputRef}
              onSendMessage={chat.sendMessage}
              disabled={chat.isLoading || !chat.isConnected}
              placeholder={
                !chat.isConnected
                  ? "Reconnecting..."
                  : config.placeholder || "Ask me anything about your data..."
              }
              isLoading={chat.isLoading}
              isMaximized={isMaximized}
              themeMode={config?.theme?.themeMode}
              accessMode={config.accessMode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
